"use client"

import * as React from "react"
import { ChevronsUpDown } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { getCurrentFacility } from "modules/facility/facilitySelectors";
import type { RootState } from "StoreTypes";
import { useAppDispatch } from "utils/hooks";
import { setCurrentFacility } from "@/ui-modules/facility-control/facilityControlSlice"
import { hasClubhouseAdminClaim } from "@/utils/roles/rolesAndClaimsHelpers"
import { setClubhouseAdmin } from "@/modules/user/userSlice"
import { useSelector } from "react-redux"
import { getUserIdFromToken } from "@/modules/user/userHooks"
import {
  resolveSelectedFacilityId,
  useFacilitiesByUserId,
  useSetCurrentLocalFacilityId,
} from "@/modules/facility/facilityHooks"
import { getLocalStorageValue, LocalStorageItems, localStorageValueToBoolean } from "@/utils/hooks/localStorage"
import { useState } from "react"

const logoIcon = "/logo.png";
const logoIconWhite = "/logo-white.png";

export const FacilitySwitcher: React.FC = () => {
  const { isMobile } = useSidebar()
  const dispatch = useAppDispatch();

  const selectedFacility = useSelector((state: RootState) => getCurrentFacility(state));

  const userId = getUserIdFromToken();
  const [userFacilities] = useFacilitiesByUserId(userId);

  const currentFacilityId = useSelector((state: RootState) => state.facility.currentFacilityId);
  const resolvedFacilityId = resolveSelectedFacilityId(
    currentFacilityId,
    userFacilities.map((facility) => facility.id),
  );
  useSetCurrentLocalFacilityId(
    resolvedFacilityId,
    resolvedFacilityId !== "" && resolvedFacilityId !== currentFacilityId,
  );

  const handleSetSelectedFacility = (value: string) => {
    dispatch(setCurrentFacility({ currentFacility: value }));
    hasClubhouseAdminClaim(value).then((clubhouseAdminAccess) => {
      dispatch(setClubhouseAdmin({ clubhouseAdmin: clubhouseAdminAccess }));
    });
  };

  const [isDarkMode] = useState(() =>
    localStorageValueToBoolean(getLocalStorageValue(LocalStorageItems.DARKMODE))
      ? true
      : false,
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full">
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground px-2"
            >
              <div className="flex size-10 items-center justify-center rounded-lg text-sidebar-primary-foreground">
                <img
                  src={isDarkMode ? logoIconWhite : logoIcon}
                  className={`logo size-10`}
                  alt="aboutGolf header logo"
                />
              </div>

              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate poppins font-semibold">Clubhouse</span>
                <span className="truncate text-xs font-medium text-text-faint">{selectedFacility?.name}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg ring-0"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Facilities
              </DropdownMenuLabel>
              {userFacilities.map((facility, index) => (
                <DropdownMenuItem
                  key={facility.id}
                  onClick={() => handleSetSelectedFacility(facility.id)}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border">
                    <img
                      src={isDarkMode ? logoIconWhite : logoIcon}
                      className="size-3.5 shrink-0"
                      alt="aboutGolf header logo"
                    />
                  </div>
                  {facility.name}
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
